import { useFormik } from 'formik';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  BUTTON_TYPES,
  FEEDBACK_TEXTAREA_LIMIT,
  HR_OVERALL_FEEDBACK,
  HR_RESULT_DROPDOWN_OPTIONS,
  INTERVIEW_ROUND_RESULT,
  InterviewType,
  LABEL_TYPES
} from '../../constants/common';
import { addFeedback } from '../../redux/actions/interviews';
import { interviewDetailAction } from '../../redux/actions/userDetails';
import {
  interviewerFeedbackDetailApi,
  overallInterviewerFeedbackDetailApi
} from '../../redux/apis/userDetails';
import CustomButton from '../CustomButton';
import Label from '../Label';
import { TextAreaWithCounterLimit } from '../TextAreaWithCounterLimit';

const UpdateHrResultPopup = ({
  show,
  onHide,
  id,
  currentResult,
  candidateName,
  existingPackage = '',
  existingExpectation = '',
  existingNote = '',
  tableStates,
  parentInterviewId
}) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.apiStatus);

  const resolveResultCode = (result) => {
    if (!result) return '1';
    const str = String(result);
    const byCode = HR_OVERALL_FEEDBACK.find((item) => item.code === str);
    if (byCode) return byCode.code;
    const byName = HR_OVERALL_FEEDBACK.find(
      (item) => item.name.toLowerCase() === str.toLowerCase()
    );
    if (byName) return byName.code;
    return str;
  };

  const isPostSelectionCode = (code) => ['3', '4', '7', '6', '5'].includes(String(code));

  const getRatingResultFromSources = (...sources) => {
    for (const val of sources) {
      if (val === undefined || val === null || val === '') continue;
      const code = resolveResultCode(val);
      if (isPostSelectionCode(code)) {
        return code;
      }
    }
    return '';
  };

  const initialRatingCode = getRatingResultFromSources(currentResult);
  const [activeCode, setActiveCode] = useState(
    () => initialRatingCode || resolveResultCode(currentResult)
  );
  const [invitationIds, setInvitationIds] = useState('');

  useEffect(() => {
    if (currentResult) {
      const code = getRatingResultFromSources(currentResult);
      setActiveCode(code || resolveResultCode(currentResult));
    }
  }, [currentResult]);

  const currentStatusName = INTERVIEW_ROUND_RESULT[activeCode] || 'Selected';

  // Only allowed post-selection options: Offered, In Discussion, Further Review, Offer Accepted, Offer Rejected
  const hrResultOptions = HR_RESULT_DROPDOWN_OPTIONS;

  const defaultResult = initialRatingCode || '3';

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      result: defaultResult,
      candidateOfferedSalary: existingPackage || '',
      candidateExpectation: existingExpectation || '',
      feedbackNote: existingNote || ''
    },
    validationSchema: Yup.object({
      result: Yup.string().required('Please select result.'),
      candidateOfferedSalary: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null || originalValue === undefined
            ? null
            : value
        )
        .when('result', {
          is: '3',
          then: Yup.number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null || originalValue === undefined
                ? null
                : value
            )
            .typeError('Only positive numbers are allowed.')
            .min(0, 'Offered salary should not be less than 0.')
            .max(99, 'Offered salary should not be more than 99.')
            .required('Please enter offered salary.'),
          otherwise: Yup.number()
            .transform((value, originalValue) =>
              originalValue === '' || originalValue === null || originalValue === undefined
                ? null
                : value
            )
            .typeError('Only positive numbers are allowed.')
            .min(0, 'Offered salary should not be less than 0.')
            .max(99, 'Offered salary should not be more than 99.')
            .nullable()
        }),
      candidateExpectation: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null || originalValue === undefined
            ? null
            : value
        )
        .typeError('Only positive numbers are allowed.')
        .min(0, 'Candidate expectation should not be less than 0.')
        .max(99, 'Candidate expectation should not be more than 99.')
        .nullable()
    }),
    onSubmit: (values) => {
      const selectedRatingResult = values.result.toString();
      const noteToSend =
        values.feedbackNote?.trim() || existingNote?.trim() || 'HR feedback updated';

      const formData = {
        interviewRoundId: id,
        interviewType: InterviewType['HR Round'],
        result: '1',
        ratingResult: selectedRatingResult,
        feedbackNote: noteToSend,
        ...(values.candidateOfferedSalary?.toString().trim() && {
          candidateOfferedSalary: values.candidateOfferedSalary.toString().trim(),
          packagePerAnum: values.candidateOfferedSalary.toString().trim()
        }),
        ...(values.candidateExpectation?.toString().trim() && {
          candidateExpectations: values.candidateExpectation.toString().trim()
        }),
        ...(invitationIds && { invitationIds })
      };

      dispatch(addFeedback(formData));

      setTimeout(() => {
        dispatch(interviewDetailAction(tableStates));
      }, 500);
      handleClose();
    }
  });

  // Fetch latest notes, offered salary, expectation, and ratingResult when opening the popup
  useEffect(() => {
    let isMounted = true;
    if (show && id) {
      Promise.allSettled([
        interviewerFeedbackDetailApi(id),
        parentInterviewId
          ? overallInterviewerFeedbackDetailApi(parentInterviewId)
          : Promise.resolve(null)
      ]).then(([roundRes, interviewRes]) => {
        if (!isMounted) return;

        const roundData =
          roundRes.status === 'fulfilled'
            ? roundRes.value?.data?.data || roundRes.value?.data
            : null;
        const interviewData =
          interviewRes.status === 'fulfilled'
            ? interviewRes.value?.data?.data || interviewRes.value?.data
            : null;

        const parseJSONSafely = (val) => {
          if (!val) return {};
          if (typeof val === 'object') return val;
          if (typeof val === 'string') {
            try {
              return JSON.parse(val);
            } catch {
              return {};
            }
          }
          return {};
        };

        const roundRatings = parseJSONSafely(roundData?.Ratings);
        const interviewRatings = parseJSONSafely(interviewData?.Ratings);

        const fetchedNote =
          roundData?.FeedbackNote ||
          roundData?.InterviewRoundFeedback ||
          roundRatings?.FeedbackNote ||
          interviewData?.FeedbackNote ||
          interviewRatings?.FeedbackNote;

        const fetchedSalary =
          roundData?.CandidateOfferedSalary ||
          roundRatings?.CandidateOfferedSalary ||
          roundRatings?.Package ||
          roundRatings?.PackagePerAnum ||
          roundData?.Package ||
          interviewRatings?.CandidateOfferedSalary ||
          interviewRatings?.Package ||
          interviewData?.CandidateOfferedSalary;

        const fetchedExpectation =
          roundData?.CandidateExpectations ||
          roundRatings?.CandidateExpectations ||
          interviewRatings?.CandidateExpectations ||
          interviewData?.CandidateExpectations;

        // Select and set ratingResult from GET API
        const fetchedRatingResult = getRatingResultFromSources(
          roundData?.ratingResult,
          roundRatings?.ratingResult,
          roundRatings?.Result,
          roundRatings?.OverAllFeedback,
          roundData?.Result,
          interviewData?.Result,
          interviewRatings?.ratingResult,
          interviewRatings?.Result
        );

        if (fetchedNote !== undefined && fetchedNote !== null && fetchedNote !== '') {
          formik.setFieldValue('feedbackNote', String(fetchedNote));
        }
        if (fetchedSalary !== undefined && fetchedSalary !== null && fetchedSalary !== '') {
          formik.setFieldValue('candidateOfferedSalary', String(fetchedSalary));
        }
        if (
          fetchedExpectation !== undefined &&
          fetchedExpectation !== null &&
          fetchedExpectation !== ''
        ) {
          formik.setFieldValue('candidateExpectation', String(fetchedExpectation));
        }

        const acceptedInvites =
          roundData?.InterviewRoundInvitations?.filter(
            (invite) => invite.Status === 'Accept' || invite.Status === 1
          ).map((invite) => invite.ID) || [];
        if (acceptedInvites.length) {
          setInvitationIds(acceptedInvites.join(','));
        }

        if (fetchedRatingResult) {
          formik.setFieldValue('result', fetchedRatingResult);
          setActiveCode(fetchedRatingResult);
        } else {
          const fallbackCode = resolveResultCode(
            roundData?.Result || interviewData?.Result || currentResult
          );
          if (fallbackCode) {
            setActiveCode(fallbackCode);
          }
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [show, id, parentInterviewId]);

  const handleClose = () => {
    formik.resetForm();
    onHide();
  };

  const getBadgeSeverity = (code) => {
    switch (String(code)) {
      case '1':
      case '6':
        return 'success';
      case '2':
      case '5':
        return 'danger';
      case '3':
      case '4':
      case '7':
        return 'info';
      case '8':
        return 'warning';
      default:
        return null;
    }
  };

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
  };

  return (
    <Dialog
      header="Update HR Result"
      className={classNames('card overflow-hidden resize-none feedback-dialog', {
        hidden: isLoading
      })}
      visible={show}
      onHide={handleClose}
      closable
      draggable={false}
      resizable={false}>
      <div className="form-box-wrapper feedback-form-popup">
        {/* Current Status and Candidate Header */}
        <div className="flex align-items-center justify-content-between mb-3 pb-2 border-bottom-1 surface-border">
          <div className="flex align-items-center">
            <span className="text-secondary font-medium mr-2">Current Status:</span>
            <Badge
              value={currentStatusName}
              severity={getBadgeSeverity(activeCode)}
              className="text-sm px-2 py-1 font-semibold"
            />
          </div>
          {candidateName && (
            <div className="text-secondary font-medium text-sm">
              <i className="pi pi-user mr-1" />
              <span>{candidateName}</span>
            </div>
          )}
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="form-row-wrapper">
            {/* Result Dropdown */}
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="result" text="Result" isMandatory />
                  <Dropdown
                    name="result"
                    id="result"
                    optionLabel="name"
                    optionValue="code"
                    value={formik.values.result}
                    options={hrResultOptions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Select result"
                    className={classNames({
                      'p-invalid': isFormFieldValid('result'),
                      'w-full': true
                    })}
                    panelClassName="custom-dropdown-panel"
                  />
                  {getFormErrorMessage('result')}
                </div>
              </div>
            </div>

            {/* Salary & Expectation Fields */}
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label
                    htmlFor="candidateOfferedSalary"
                    text="Offered Salary"
                    isMandatory={formik.values.result === '3'}
                  />
                  <div className="p-inputgroup w-full">
                    <InputText
                      id="candidateOfferedSalary"
                      name="candidateOfferedSalary"
                      value={formik.values.candidateOfferedSalary}
                      onChange={formik.handleChange}
                      className={classNames({
                        'p-invalid': isFormFieldValid('candidateOfferedSalary'),
                        'w-full': true
                      })}
                      onBlur={formik.handleBlur}
                      placeholder="Offered Salary"
                      keyfilter={'money'}
                    />
                    <span className="p-inputgroup-addon add-candidate-lpa">LPA</span>
                  </div>
                  {getFormErrorMessage('candidateOfferedSalary')}
                </div>
              </div>
            </div>

            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="candidateExpectation" text={LABEL_TYPES.CANDIDATE_EXPECTATION} />
                  <div className="p-inputgroup w-full">
                    <InputText
                      id="candidateExpectation"
                      name="candidateExpectation"
                      value={formik.values.candidateExpectation}
                      onChange={formik.handleChange}
                      className={classNames({
                        'p-invalid': isFormFieldValid('candidateExpectation'),
                        'w-full': true
                      })}
                      onBlur={formik.handleBlur}
                      placeholder="Candidate Expectation"
                      keyfilter={'money'}
                    />
                    <span className="p-inputgroup-addon add-candidate-lpa">LPA</span>
                  </div>
                  {getFormErrorMessage('candidateExpectation')}
                </div>
              </div>
            </div>

            {/* Note Field */}
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="feedbackNote" text="Note" />
                  <TextAreaWithCounterLimit
                    placeholder="Add Note"
                    value={formik.values.feedbackNote}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={
                      formik.touched['feedbackNote'] ? formik.errors['feedbackNote'] : ''
                    }
                    className={classNames({
                      'p-invalid': isFormFieldValid('feedbackNote')
                    })}
                    autoResize
                    id="feedbackNote"
                    name="feedbackNote"
                    htmlFor="feedbackNote"
                    maxLimit={FEEDBACK_TEXTAREA_LIMIT.MAX}
                    rows={3}
                    cols={30}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dialog Actions */}
          <div className="form-btn-wrapper justify-flex-end flex gap-2 mt-2">
            <CustomButton
              variant="contained"
              onClick={handleClose}
              color="error"
              className="gray-btn mr-2 w-auto"
              disabled={isLoading}>
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
            <CustomButton type="submit" variant="contained" disabled={isLoading} className="w-auto">
              {BUTTON_TYPES.SAVE}
            </CustomButton>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default UpdateHrResultPopup;
